/**
 * Аналитика прошлых лет — типы и запросы.
 *
 * Бэкенд: `GET /api/v1/analytics/history/{crops,fields,fields/:id}`
 * (`backend/src/ikolhoz/api/v1/analytics_history.py`).
 *
 * Единицы: урожайность приходит уже в **ц/га** и уже **бункерным весом** —
 * фронт ничего не пересчитывает и не переименовывает (решения В и Г founder'а
 * 07.08). Вал — в тоннах, на экране вторым планом (решение Ж).
 *
 * `null` в урожайности означает «не знаем» и обязан рисоваться прочерком.
 * `0` — это «убрали и не получили»: реальный факт, который показывается
 * цифрой. Подмена одного другим — самая дорогая ошибка на этом экране.
 */
import { api } from '@/lib/api';

/** Один год культуры/поля. */
export interface HistoryYear {
  year: number;
  area_ha: number;
  /** ц/га, бункерный вес. `null` = уборку не закрыли. */
  yield_c_ha: number | null;
  /** Вал, т. */
  gross_t: number | null;
  sowings_count: number;
  sowings_with_yield: number;
  /** `false` = год не закрыт, на экране «не финал». */
  is_final: boolean;
}

export interface HistorySummary {
  years_count: number;
  years_with_yield: number;
  avg_area_ha: number | null;
  /** Взвешенная по площади средняя, ц/га. Именно она уходит в план. */
  avg_yield_c_ha: number | null;
  min_yield_c_ha: number | null;
  max_yield_c_ha: number | null;
  avg_gross_t: number | null;
  total_area_ha: number;
  sowings_count: number;
  sowings_with_yield: number;
}

export interface CropHistoryRow {
  key: string;
  kind: 'crop' | 'field_state';
  crop_id: string | null;
  field_state_id: string | null;
  name: string;
  color: string | null;
  /**
   * `false` → «не применимо» вместо урожайности. Это пар / АМП в обеих
   * ипостасях: состояние поля И культура-пар каталога (`is_fallow`) — у
   * второй `kind` остаётся `'crop'`, поэтому смотреть надо на этот флаг.
   */
  yield_applicable: boolean;
  /**
   * Посевы с урожайностью внутри строки пара/АМП. Цифра урожая на экран не
   * попадает никогда, но и не глотается молча: такие посевы почти наверняка
   * классифицированы ошибочно — счётчик уезжает в заметку «требует разбора».
   */
  stray_yield_sowings: number;
  summary: HistorySummary;
  /** Тот же свод без незакрытого года — тумблер работает без запроса в сеть. */
  summary_excl_current: HistorySummary;
  years: HistoryYear[];
}

export interface HistoryPeriod {
  year_from: number;
  year_to: number;
  current_year: number;
  years: number[];
}

export interface HistoryCoverage {
  years_count: number;
  crops_count: number;
  fields_count: number;
  sowings_count: number;
  sowings_with_yield: number;
  with_yield_pct: number | null;
}

export interface CropsHistoryResponse {
  period: HistoryPeriod;
  farm_id: string | null;
  company_id: string | null;
  cluster_id: string | null;
  coverage: HistoryCoverage;
  items: CropHistoryRow[];
}

export interface FieldYearCell {
  year: number;
  kind: 'crop' | 'field_state';
  crop_id: string | null;
  crop_name: string;
  color: string | null;
  /** Площадь ИМЕННО этого занятия, не всего года. */
  area_ha: number;
  /**
   * Урожайность доминирующего занятия — его собственная, не смесь по году.
   * Пересев «пшеница → ячмень» не имеет общей урожайности: такого числа
   * не было ни у одной из культур.
   */
  yield_c_ha: number | null;
  gross_t: number | null;
  sowings_count: number;
  is_final: boolean;
  /** Вся площадь занятий года, включая остальные культуры. */
  total_area_ha: number;
  /** Остальные культуры года — маркер «в году было не только это». */
  other_crops: string[];
}

export interface FieldHistoryRow {
  field_id: string;
  name: string;
  number: string | null;
  farm_name: string | null;
  brigade_name: string | null;
  area_ha: number | null;
  /** Раньше этого года поля в истории не было — рисуем штриховку, не ноль. */
  first_history_year: number | null;
  last_history_year: number | null;
  cells: FieldYearCell[];
  avg_yield_c_ha: number | null;
  avg_crop_id: string | null;
  avg_crop_name: string | null;
  avg_years_count: number;
}

export interface FieldsHistoryResponse {
  period: HistoryPeriod;
  farm_id: string | null;
  company_id: string | null;
  cluster_id: string | null;
  crop_id: string | null;
  total: number;
  limit: number;
  offset: number;
  items: FieldHistoryRow[];
}

export interface FieldCardYearRow extends FieldYearCell {
  /** Откуда цифра: импорт / завели у нас / и то и другое. */
  source: 'agrohistory' | 'manual' | 'mixed' | string;
}

export interface FieldCardResponse {
  field_id: string;
  name: string;
  number: string | null;
  farm_name: string | null;
  brigade_name: string | null;
  area_ha: number | null;
  period: HistoryPeriod;
  rows: FieldCardYearRow[];
  crops_count: number;
  /** Годы, где поле отдыхало ЦЕЛИКОМ. Год «половина пара» паровым не считается. */
  fallow_years: number;
  years_count: number;
  lead_crop_name: string | null;
  lead_crop_summary: HistorySummary | null;
}

export interface HistoryFilters {
  farmId?: string | null;
  /**
   * Юрлицо (из хаба планирования, AK-541). Без него «все хозяйства» в холдинге
   * смешают урожайность разных компаний в одно число — и оно уедет в бюджет
   * одной из них.
   */
  companyId?: string | null;
  /**
   * Кластер = все его хозяйства. Кластер не отдельная сущность в данных
   * урожайности, поэтому бэк разворачивает его в набор `farms` — как это уже
   * делает `build_scope_filter` для scope-типа `cluster`.
   */
  clusterId?: string | null;
  /**
   * Явный локальный выбор «вся компания». Обязан уезжать на бэк ФЛАГОМ
   * (`whole_company=1`): запрос «без farm_id и cluster_id» бэк не отличает от
   * старого клиента и применяет линзу сессии — ровно так на staging v1.1742
   * «вся компания» показывала данные фермы под шапкой компании (UAT 21.08).
   * Любой явный выбор экрана замещает линзу сессии на стороне бэка.
   */
  wholeCompany?: boolean;
  cropId?: string | null;
  yearFrom?: number;
  yearTo?: number;
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function getCropsHistory(f: HistoryFilters): Promise<CropsHistoryResponse> {
  return api.get<CropsHistoryResponse>(
    `/api/v1/analytics/history/crops${qs({
      farm_id: f.farmId,
      company_id: f.companyId,
      cluster_id: f.clusterId,
      whole_company: f.wholeCompany ? 1 : undefined,
      year_from: f.yearFrom,
      year_to: f.yearTo,
    })}`,
  );
}

export function getFieldsHistory(
  f: HistoryFilters & { search?: string; limit?: number; offset?: number },
): Promise<FieldsHistoryResponse> {
  return api.get<FieldsHistoryResponse>(
    `/api/v1/analytics/history/fields${qs({
      farm_id: f.farmId,
      company_id: f.companyId,
      cluster_id: f.clusterId,
      whole_company: f.wholeCompany ? 1 : undefined,
      crop_id: f.cropId,
      year_from: f.yearFrom,
      year_to: f.yearTo,
      search: f.search,
      limit: f.limit,
      offset: f.offset,
    })}`,
  );
}

export function getFieldHistoryCard(
  fieldId: string,
  // Карточка несёт ТОТ ЖЕ локальный выбор области, что и матрица: клик по
  // строке видимой матрицы обязан открывать карточку. Без параметров бэк
  // применил бы линзу сессии и ответил 404 на поле чужой (для линзы) фермы.
  f: Pick<HistoryFilters, 'farmId' | 'clusterId' | 'wholeCompany' | 'yearFrom' | 'yearTo'> = {},
): Promise<FieldCardResponse> {
  return api.get<FieldCardResponse>(
    `/api/v1/analytics/history/fields/${fieldId}${qs({
      farm_id: f.farmId,
      cluster_id: f.clusterId,
      whole_company: f.wholeCompany ? 1 : undefined,
      year_from: f.yearFrom,
      year_to: f.yearTo,
    })}`,
  );
}

// ─── Форматирование ─────────────────────────────────────────────────────────

/**
 * Урожайность на экран. Прочерк — «не знаем», ноль — честный ноль.
 *
 * Разделитель дробной части — запятая: агроном пишет «48,6», а не «48.6».
 */
export function fmtYield(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toFixed(1).replace('.', ',');
}

/** Площадь и вал — целые с неразрывным пробелом между разрядами. */
export function fmtArea(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return Math.round(v)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Высота столбика спарклайна в процентах от лучшего года.
 *
 * Минимум 8% — чтобы «убрали и не получили» (0 ц/га) оставался ВИДИМЫМ
 * столбиком, а не сливался с «года не было». Ноль и пропуск на этом экране
 * различаются, поэтому и рисуются по-разному.
 */
export function barPct(value: number | null, max: number): number {
  if (value === null || max <= 0) return 0;
  return Math.max(8, Math.round((value / max) * 100));
}

/**
 * Лучший год периода — база для всех относительных полос.
 *
 * Принимает любую строку с урожайностью (год культуры, ячейка поля, строка
 * карточки): «относительно лучшего года» считается одинаково везде.
 */
export function maxYield(rows: { yield_c_ha: number | null }[]): number {
  return rows.reduce((m, y) => (y.yield_c_ha !== null && y.yield_c_ha > m ? y.yield_c_ha : m), 0);
}
